import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { activityService } from '../../../services/activityService';

const RecentActivity = ({ type = 'user', setActiveTab }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const data = type === 'seller' 
          ? await activityService.getSellerActivity(user.id)
          : await activityService.getUserActivity(user.id);
        setActivities(data);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [user?.id, type]);

  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'download': return 'text-primary';
      case 'purchase': return 'text-success';
      case 'assembly': return 'text-accent';
      case 'share': return 'text-secondary';
      case 'review': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getActivityBg = (activityType) => {
    switch (activityType) {
      case 'download': return 'bg-primary/10';
      case 'purchase': return 'bg-success/10';
      case 'assembly': return 'bg-accent/10';
      case 'share': return 'bg-secondary/10';
      case 'review': return 'bg-warning/10';
      default: return 'bg-muted';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Unknown date';
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleViewAllReviews = () => {
    if (type === 'seller') {
      if (setActiveTab) {
        setActiveTab('storeReviews');
      } else {
        navigate('/seller-dashboard?tab=storeReviews');
      }
    } else {
      if (setActiveTab) {
        setActiveTab('myReviews');
      } else {
        navigate('/user-account-dashboard?tab=myReviews');
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Recent Activity</h2>
        <button
          onClick={handleViewAllReviews}
          className="text-sm text-primary hover:underline font-medium"
        >
          {type === 'seller' ? 'View Store Reviews' : 'View All Reviews'}
        </button>
      </div>

      <div className="space-y-4">
        {activities.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="History" size={24} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">No recent activity found.</p>
          </div>
        ) : (
          activities.map((activity) => (
            <div 
              key={activity.id} 
              onClick={() => {
                if (activity.link) {
                  if (setActiveTab && activity.link.includes('?tab=')) {
                    setActiveTab(activity.link.split('?tab=')[1]);
                  } else {
                    navigate(activity.link);
                  }
                }
              }}
              className={`flex items-start space-x-4 p-3 rounded-lg hover:bg-muted/50 transition-smooth group ${activity.link ? 'cursor-pointer' : ''}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${getActivityBg(activity.type)}`}>
                <Icon
                  name={activity.icon || 'Circle'}
                  size={18}
                  className={getActivityColor(activity.type)} 
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                      {activity.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                      {activity.description}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-2 font-medium">
                      {formatTimestamp(activity.timestamp)}
                    </p>
                  </div>
                  
                  {activity.image && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden ml-3 flex-shrink-0 border border-border">
                      <Image
                        src={activity.image}
                        alt={activity.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110" 
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentActivity;